/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  keyframes,
  Button,
  Autocomplete,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import T from 'i18n';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentCaseId, selectFolders } from 'common/selectors';
import { useFetchAnnotations } from 'features/viewing/redux/fetchAnnotations';
import api from 'common/api';
import { ExistingHyperlinkRow } from '../../extraComponents/hyperlinks/ExistingHyperLinkRow';
import { Spinner } from 'features/common';
import moment from 'moment';
import * as actions from '../../../redux/actions';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { flattenFolders } from 'utils/foldersStructuring';
import { CheckCircle, FilterListRounded } from '@mui/icons-material';

const underlineAnimation = keyframes`
  0% {
    text-decoration: underline;
  }
  75% {
    text-decoration: underline;
  }
  100% {
    text-decoration: none;
  }
`;

const useStyles = makeStyles({
  tableContainer: {
    maxHeight: '400px',
    overflowY: 'auto',
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    borderBottom: '1px solid #ccc',
    textAlign: 'left',
    padding: '8px',
    position: 'sticky',
    top: 0,
    backgroundColor: '#f9f9f9',
    zIndex: 1,
  },
  td: {
    borderBottom: '1px solid #eee',
    padding: '8px',
  },
  existingTitle: {
    animation: `${underlineAnimation} 5s ease-in-out`,
  },
});

type ManageHyperlinksProps = {
  show: boolean;
  handleClose: () => void;
  selectedRows: any[];
};

export const ManageHyperlinks: React.FC<ManageHyperlinksProps> = ({
  show,
  handleClose,
  selectedRows,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const caseId = useSelector(selectCurrentCaseId);
  const folders = useSelector(selectFolders);

  const file: any = selectedRows?.[selectedRows.length - 1];

  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [existingHyperlinks, setExistingHyperlinks] = useState<any[]>([]);
  const [folderName, setFolderName] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState('');
  const [bundleType, setBundleType] = useState<'team-bundle' | 'private-bundle'>('team-bundle');
  const [assignmentComplete, setAssignmentComplete] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [folderError, setFolderError] = useState<string | null>(null);

  const [filterValue, setFilterValue] = useState('');

  const { fetchAnnotations } = useFetchAnnotations();

  const teamBundlesMenu = useMemo(() => flattenFolders(folders['teamBundles']), [folders]);
  const privateBundlesMenu = useMemo(() => flattenFolders(folders['privateBundles']), [folders]);

  const normalizeFolderName = (name?: string) => (name || '').replace(/\u00A0/g, ' ').trim();

  const folderOptions = useMemo(
    () => [
      ...(teamBundlesMenu || []).map((folder: any) => ({
        ...folder,
        displayName: normalizeFolderName(folder.name),
        groupLabel: T.translate('hyperlinks.assign.teamBundlesLabel', {
          defaultValue: 'Team bundles',
        }),
      })),
      ...(privateBundlesMenu || []).map((folder: any) => ({
        ...folder,
        displayName: normalizeFolderName(folder.name),
        groupLabel: T.translate('hyperlinks.assign.privateBundlesLabel', {
          defaultValue: 'Private bundles',
        }),
      })),
    ],
    [privateBundlesMenu, teamBundlesMenu],
  );

  const suggestedFolderName = useMemo(
    () => (file?.id ? `${file.id} (${moment().format('DD/MM/YYYY')})` : ''),
    [file?.id],
  );

  const folderOptionsWithSuggested = useMemo(() => {
    let options = [...folderOptions];
    if (suggestedFolderName) {
      const alreadyExists = options.some(
        (opt) => normalizeFolderName(opt.name) === normalizeFolderName(suggestedFolderName),
      );
      if (!alreadyExists) {
        options = [
          {
            id: '__suggested__',
            name: suggestedFolderName,
            displayName: suggestedFolderName,
            type: bundleType,
            groupLabel: T.translate('hyperlinks.assign.suggestedOption', {
              defaultValue: 'Suggested',
            }),
          },
          ...options,
        ];
      }
    }
    return options;
  }, [folderOptions, suggestedFolderName, bundleType]);

  useEffect(() => {
    const defaultType =
      teamBundlesMenu && teamBundlesMenu.length > 0 ? 'team-bundle' : 'private-bundle';
    setBundleType(defaultType as 'team-bundle' | 'private-bundle');
  }, [teamBundlesMenu, privateBundlesMenu]);

  useEffect(() => {
    if (!show || !file) return;
    setFolderName(suggestedFolderName);
    setSelectedFolderId('');
    const defaultType =
      teamBundlesMenu && teamBundlesMenu.length > 0 ? 'team-bundle' : 'private-bundle';
    setBundleType(defaultType as 'team-bundle' | 'private-bundle');
    setAssignmentComplete(false);
    setFolderError(null);
    getExistingHyperlinks();
  }, [show, caseId, file, suggestedFolderName, teamBundlesMenu, privateBundlesMenu]);

  const getExistingHyperlinks = async () => {
    try {
      setLoading(true);
      let existing = await fetchAnnotations({ file: file.id });
      existing = existing.filter((ann: any) => ann.annotation.type === 'TextHyperlinkAnnotation');

      existing.sort((a: any, b: any) => {
        return a?.annotation?.pageNumber - b?.annotation?.pageNumber;
      });

      setExistingHyperlinks(existing);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      if (error instanceof Error) {
        setFetchError(error.message);
      } else {
        setFetchError('Unknown error occurred');
      }
    }
  };

  const getFilteredHyperlinks = () => {
    if (!filterValue.trim()) {
      return existingHyperlinks;
    }
    const filter = filterValue.toLowerCase();

    return existingHyperlinks.filter((ann) => {
      const docId = (
        ann.annotation?.targetDocId ||
        ann.annotation?.data?.linkId ||
        ''
      ).toLowerCase();
      const targetFile = (ann.annotation?.targetFilename || '').toLowerCase();
      const sourceText = (ann.annotation?.text || '').toLowerCase(); // If you store user text
      const createdByName = (ann.createdBy?.name || ann.createdBy?.id || '').toLowerCase();
      const pageNumber = ann.annotation?.pageNumber?.toString() || '';

      return (
        docId.includes(filter) ||
        targetFile.includes(filter) ||
        sourceText.includes(filter) ||
        createdByName.includes(filter) ||
        pageNumber.includes(filter)
      );
    });
  };

  const clearAll = () => {
    setLoading(false);
    setFetchError(null);
    setExistingHyperlinks([]);
    setFolderName(suggestedFolderName);
    setSelectedFolderId('');
    const defaultType =
      teamBundlesMenu && teamBundlesMenu.length > 0 ? 'team-bundle' : 'private-bundle';
    setBundleType(defaultType as 'team-bundle' | 'private-bundle');
    setAssignmentComplete(false);
    setShowDeleteConfirmation(false);
    setFolderError(null);

    setFilterValue('');
  };

  const onClose = () => {
    clearAll();
    handleClose();
  };

  const handleDeleteAll = async () => {
    try {
      setLoading(true);
      await api.del(`/cases/${caseId}/files/${file.id}/hyperlinks`);
      handleClose();
    } catch (err) {
      setLoading(false);
      if (err instanceof Error) {
        setFetchError(err.message);
      } else {
        setFetchError('Unknown error occurred');
      }
    }
  };
  const handleDeleteIconClick = () => {
    setShowDeleteConfirmation(true);
    setTimeout(() => setShowDeleteConfirmation(false), 5000);
  };
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  const handleRefresh = () => {
    clearAll();
    getExistingHyperlinks();
  };

  const findExistingFolderByName = (name: string, typeFilter?: string) => {
    const normalizedName = normalizeFolderName(name);
    return folderOptions.find(
      (folder: any) =>
        normalizeFolderName(folder.name) === normalizedName &&
        (!typeFilter || folder.type === typeFilter),
    );
  };

  const ensureFolderExists = async () => {
    const normalizedName = normalizeFolderName(folderName);
    if (!normalizedName) {
      setFolderError(
        T.translate('hyperlinks.assign.folderNameRequired', {
          defaultValue: 'Enter a bundle name to assign hyperlinks.',
        }),
      );
      return null;
    }

    const existingFolder = findExistingFolderByName(normalizedName);
    if (existingFolder?.id) {
      if (existingFolder.type === 'team-bundle' || existingFolder.type === 'private-bundle') {
        setBundleType(existingFolder.type);
        setFolderError(null);
        return existingFolder.id;
      }
      setFolderError(
        T.translate('hyperlinks.assign.folderNameRequired', {
          defaultValue: 'Enter a bundle name to assign hyperlinks.',
        }),
      );
      return null;
    }

    const folderPayload = {
      name: normalizedName,
      type: bundleType,
    };

    const addFolderResponse = await dispatch(
      actions.addFolder(folderPayload, { suppressFeedback: true }) as any,
    );

    if (addFolderResponse && addFolderResponse.id) {
      try {
        await dispatch(actions.fetchContainer({ folder: { type: bundleType } }) as any);
      } catch {
        // Continue even if the sidebar refresh fails
      }
      setFolderError(null);
      return addFolderResponse.id;
    }

    setFolderError(
      T.translate('hyperlinks.assign.folderCreationFailed', {
        defaultValue: 'Unable to create the bundle. Please try again.',
      }),
    );
    return null;
  };

  const handleAssignFiles = async () => {
    const docs = existingHyperlinks.map((ann: any) => ({
      id: ann?.annotation?.targetDocId || ann?.annotation?.data?.linkId,
      name: ann?.annotation?.targetFilename || ann?.annotation?.data?.linkName,
    }));

    const seen = new Set<string>();
    const uniqueDocs = docs.filter((doc) => {
      if (!doc.id) return false;
      if (seen.has(doc.id)) {
        return false;
      }
      seen.add(doc.id);
      return true;
    });

    if (!uniqueDocs.length) {
      console.warn('No doc IDs found in existingHyperlinks.');
      return;
    }

    const normalizedName = normalizeFolderName(folderName);
    if (!normalizedName) {
      setFolderError(
        T.translate('hyperlinks.assign.folderNameRequired', {
          defaultValue: 'Enter a bundle name to assign hyperlinks.',
        }),
      );
      return;
    }

    try {
      setAssigning(true);
      const ensuredFolderId = selectedFolderId || (await ensureFolderExists());

      if (!ensuredFolderId) {
        setAssigning(false);
        return;
      }

      await api.post(
        `/cases/${caseId}/folders/${ensuredFolderId}/files?includeFileList=false`,
        uniqueDocs,
      );
      setAssignmentComplete(true);
      setSelectedFolderId(ensuredFolderId);
      setFolderError(null);
    } catch (error) {
      console.error('Error assigning hyperlinks:', error);
      setFolderError(
        T.translate('hyperlinks.assign.failure', {
          defaultValue: 'Unable to assign hyperlinks. Please try again.',
        }),
      );
    } finally {
      setAssigning(false);
    }
  };

  const handleAssignAgain = () => {
    setAssignmentComplete(false);
    setFolderName(suggestedFolderName);
    setSelectedFolderId('');
    const defaultType =
      teamBundlesMenu && teamBundlesMenu.length > 0 ? 'team-bundle' : 'private-bundle';
    setBundleType(defaultType as 'team-bundle' | 'private-bundle');
    setFolderError(null);
  };

  const displayedHyperlinks = getFilteredHyperlinks();

  const handleExportCsv = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (!displayedHyperlinks.length) return;

    const headers = [
      T.translate('hyperlinks.preview.sourcePageHeader', { defaultValue: 'Source Page' }),
      T.translate('hyperlinks.preview.sourceTextHeader', { defaultValue: 'Source Text' }),
      T.translate('hyperlinks.preview.docIdHeader', { defaultValue: 'Target Doc Id.' }),
      T.translate('hyperlinks.preview.createdBy', { defaultValue: 'Created By' }),
      T.translate('hyperlinks.preview.linkPageHeader', { defaultValue: 'Target FileName' }),
    ];

    const escapeCsv = (value: any) => {
      const str = value === null || value === undefined ? '' : String(value);
      return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };

    const rows = displayedHyperlinks.map((ann) => {
      const sourcePage = ann?.annotation?.pageNumber ?? '';
      const sourceText =
        ann?.annotation?.selectedText ?? ann?.annotation?.text ?? ann?.annotation?.data?.text ?? '';
      const targetDocId =
        ann?.annotation?.data?.linkId ?? ann?.annotation?.targetDocId ?? ann?.annotation?.id ?? '';
      const createdBy = ann?.createdBy?.name || ann?.createdBy?.id || '';
      const targetFilename =
        ann?.fileName ||
        ann?.annotation?.targetFilename ||
        ann?.annotation?.data?.linkName ||
        '';
      return [sourcePage, sourceText, targetDocId, createdBy, targetFilename];
    });

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => escapeCsv(cell)).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `hyperlinks-${file?.id || 'export'}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={show} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {T.translate('hyperlinks.preview.title', {
          fileId: file?.id,
          defaultValue: `Manage ${existingHyperlinks?.length} Existing Hyperlinks for ${file?.id}`,
        })}
        <IconButton size="small" onClick={onClose} sx={{ ml: 2 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.tableContainer}>
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          mb={2}
          sx={{
            borderBottom: '1px solid #ccc',
            pb: 1,
            justifyContent: 'space-between',
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <FolderCopyIcon fontSize="small" sx={{ mt: '22px', color: 'gray' }} />
            <Autocomplete
              size="medium"
              sx={{
                minWidth: 240,
                '& .MuiAutocomplete-endAdornment': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                },
              }}
              freeSolo
              options={folderOptionsWithSuggested || []}
              filterOptions={(opts) => opts}
              groupBy={(option) => option.groupLabel}
              getOptionLabel={(option: any) =>
                typeof option === 'string'
                  ? option
                  : option?.displayName || normalizeFolderName(option?.name)
              }
              forcePopupIcon
              popupIcon={<ArrowDropDownIcon fontSize="small" />}
              slotProps={{
                popupIndicator: { sx: { visibility: 'visible', opacity: 1, display: 'flex' } },
                clearIndicator: { sx: { visibility: 'visible', opacity: 1, display: 'flex' } },
              }}
              isOptionEqualToValue={(option, value) => option.id === (value as any)?.id}
              noOptionsText={T.translate('generic.noOptions', { defaultValue: 'No options' })}
              value={
                folderOptionsWithSuggested.find((val: any) => val.id === selectedFolderId) || null
              }
              inputValue={folderName}
              openOnFocus
              onChange={(e, value: any) => {
                if (typeof value === 'string') {
                  setFolderName(value);
                  setSelectedFolderId('');
                  const defaultType =
                    teamBundlesMenu && teamBundlesMenu.length > 0
                      ? 'team-bundle'
                      : 'private-bundle';
                  setBundleType(defaultType as 'team-bundle' | 'private-bundle');
                } else if (value && value.id) {
                  setFolderName(value.displayName || normalizeFolderName(value.name));
                  setSelectedFolderId(value.id === '__suggested__' ? '' : value.id);
                  if (value.type === 'team-bundle' || value.type === 'private-bundle') {
                    setBundleType(value.type);
                  }
                } else {
                  setFolderName('');
                  setSelectedFolderId('');
                  const defaultType =
                    teamBundlesMenu && teamBundlesMenu.length > 0
                      ? 'team-bundle'
                      : 'private-bundle';
                  setBundleType(defaultType as 'team-bundle' | 'private-bundle');
                }
                setAssignmentComplete(false);
                setFolderError(null);
              }}
              onInputChange={(event, value, reason) => {
                if (reason === 'input') {
                  setFolderName(value);
                  setSelectedFolderId('');
                  const defaultType =
                    teamBundlesMenu && teamBundlesMenu.length > 0
                      ? 'team-bundle'
                      : 'private-bundle';
                  setBundleType(defaultType as 'team-bundle' | 'private-bundle');
                  setAssignmentComplete(false);
                  setFolderError(null);
                } else if (reason === 'clear' || reason === 'reset') {
                  setFolderName('');
                  setSelectedFolderId('');
                  const defaultType =
                    teamBundlesMenu && teamBundlesMenu.length > 0
                      ? 'team-bundle'
                      : 'private-bundle';
                  setBundleType(defaultType as 'team-bundle' | 'private-bundle');
                  setAssignmentComplete(false);
                  setFolderError(null);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  label={T.translate('hyperlinks.assign.folderLabel', {
                    defaultValue: 'Bundle name',
                  })}
                  placeholder={T.translate('hyperlinks.assign.folderPlaceholder', {
                    defaultValue: 'Select or enter a bundle name',
                  })}
                />
              )}
            />
            {!assignmentComplete && (
              <Tooltip
                title={T.translate('hyperlinks.assign.tooltip', {
                  defaultValue: 'Assign Existing Hyperlinks to another folder',
                })}
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleAssignFiles}
                    disabled={!folderName || !existingHyperlinks.length || loading || assigning}
                    sx={{ mt: '22px' }}
                  >
                    {T.translate('hyperlinks.assign.button', { defaultValue: 'Assign' })}
                  </Button>
                </span>
              </Tooltip>
            )}
            {assignmentComplete && (
              <Box display="flex" gap={1} alignItems="center" sx={{ mt: '22px' }}>
                <CheckCircle color="success" />
                <Typography variant="body2">
                  {T.translate('hyperlinks.copyTo', { defaultValue: 'Copy to' })}{' '}
                  <strong>{folderName}</strong>
                </Typography>
                <Button size="small" variant="outlined" onClick={handleAssignAgain}>
                  {T.translate('hyperlinks.assignAgain', { defaultValue: 'Assign Again' })}
                </Button>
              </Box>
            )}
          </Box>

          <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
            <Box display="flex" flexDirection="row" alignContent="center">
              <TextField
                variant="standard"
                placeholder="Filter"
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                sx={{ minWidth: 140, borderColor: 'gray' }}
                InputProps={{
                  endAdornment: filterValue && (
                    <Tooltip title="Clear Filter">
                      <IconButton
                        onClick={() => setFilterValue('')}
                        edge="end"
                        size="small"
                        sx={{ color: 'gray' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
              <FilterListRounded sx={{ color: 'gray', mr: 1 }} />
            </Box>
            {showDeleteConfirmation && (
              <Box display="flex" gap={1} alignItems="center">
                <Tooltip title={T.translate('generic.confirm', { defaultValue: 'Confirm Delete' })}>
                  <IconButton
                    onClick={handleDeleteAll}
                    disabled={loading}
                    color="error"
                    size="small"
                  >
                    <CheckCircle fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={T.translate('generic.cancel', { defaultValue: 'Cancel Delete' })}>
                  <IconButton onClick={handleCancelDelete} disabled={loading} size="small">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            <Tooltip
              title={T.translate('hyperlinks.deleteAll.tooltip', {
                defaultValue: 'Delete All My Hyperlinks',
              })}
            >
              <span>
                <IconButton
                  onClick={handleDeleteIconClick}
                  disabled={loading || existingHyperlinks.length === 0}
                  color="error"
                  size="small"
                >
                  <DeleteForeverIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip
              title={T.translate('hyperlinks.refresh.tooltip', { defaultValue: 'Refresh List' })}
            >
              <span>
                <IconButton onClick={handleRefresh} disabled={loading} size="small">
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={T.translate('hyperlinks.assign.exportCsv', { defaultValue: 'Export to CSV' })}
            >
              <span>
                <IconButton
                  onClick={handleExportCsv}
                  disabled={!displayedHyperlinks.length || loading}
                  size="small"
                >
                  <FileDownloadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {folderError && (
          <Typography variant="body2" color="error" sx={{ mb: 1, mt: -1 }}>
            {folderError}
          </Typography>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" my={3}>
            <Spinner />
          </Box>
        ) : fetchError ? (
          <Typography variant="body1" color="error">
            {fetchError}
          </Typography>
        ) : displayedHyperlinks.length === 0 ? (
          <Typography variant="body2" color="textSecondary" mt={2}>
            {T.translate('hyperlinks.preview.noData', {
              defaultValue: 'No hyperlink candidates.',
            })}
          </Typography>
        ) : (
          <Box mt={2}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th className={classes.th}>
                    {T.translate('hyperlinks.preview.sourcePageHeader', {
                      defaultValue: 'Source Page',
                    })}
                  </th>
                  <th className={classes.th}>
                    {T.translate('hyperlinks.preview.sourceTextHeader', {
                      defaultValue: 'Source Text',
                    })}
                  </th>
                  <th className={classes.th}>
                    {T.translate('hyperlinks.preview.docIdHeader', {
                      defaultValue: 'Target Doc Id.',
                    })}
                  </th>
                  <th className={classes.th}>
                    {T.translate('hyperlinks.preview.createdBy', {
                      defaultValue: 'Created By',
                    })}
                  </th>
                  <th className={classes.th}>
                    {T.translate('hyperlinks.preview.linkPageHeader', {
                      defaultValue: 'Target FileName',
                    })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayedHyperlinks.map((ann, idx) => (
                  <ExistingHyperlinkRow key={idx} ann={ann} classes={classes} />
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ color: '#748CEC' }}>
          {T.translate('hyperlinks.preview.cancel', {
            defaultValue: 'Close',
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageHyperlinks;
